// Session context: the single owner of "who am I right now".
//
// It revalidates on focus and on an explicit signal from other tabs, keeps the
// effective account id for scoping queries, and clears all private query data
// on logout or when the effective account changes (including impersonation).

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@/api/errors';
import * as api from '@/api/endpoints';
import type { Session } from '@/api/endpoints';
import { queryKeys } from '@/query/keys';

export type SessionState =
  | { status: 'loading' }
  | { status: 'authenticated'; session: Session }
  | { status: 'anonymous' }
  | { status: 'offline'; lastSession: Session | null; error: ApiError };

interface SessionContextValue {
  state: SessionState;
  /** Effective account id, available once authenticated. */
  accountId: string | null;
  /** Whether the current session is an admin acting normally (not impersonating). */
  isAdmin: boolean;
  /** Whether the current session is impersonating another account. */
  isImpersonating: boolean;
  /** Whether the actor is an admin (true even while impersonating). */
  actorIsAdmin: boolean;
  /** Force a refetch of the session (for example after impersonation). */
  refresh: () => Promise<SessionState>;
  /** Mark the session anonymous locally and clear private data. */
  clear: () => void;
  /** Broadcast a local session change so other tabs revalidate. */
  broadcastChange: () => void;
}

const SessionContext = createContext<SessionContextValue | null>(null);

/** Cross-tab channel for session changes. Sends no sensitive data. */
const SESSION_STORAGE_KEY = 'sensing.session.changed';

function isOffline(error: unknown): boolean {
  // A network failure means we cannot know the session state; a 401 means we do.
  return error instanceof ApiError && (error.code === 'network_error' || error.code === 'aborted');
}

export function SessionProvider({ children }: { children: ReactNode }): ReactNode {
  const queryClient = useQueryClient();
  const [overridden, setOverridden] = useState<SessionState | null>(null);
  const previousAccountId = useRef<string | null>(null);

  const sessionQuery = useQuery({
    queryKey: queryKeys.session,
    queryFn: ({ signal }) => api.getSession({ signal }),
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.isUnauthenticated) return false;
      return failureCount < 2;
    },
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    staleTime: 30_000,
  });

  const liveState: SessionState = useMemo(() => {
    if (sessionQuery.isSuccess && sessionQuery.data) {
      return { status: 'authenticated', session: sessionQuery.data };
    }
    if (sessionQuery.isPending) return { status: 'loading' };
    if (sessionQuery.error) {
      if (sessionQuery.error instanceof ApiError) {
        if (sessionQuery.error.isUnauthenticated) return { status: 'anonymous' };
        if (isOffline(sessionQuery.error)) {
          return { status: 'offline', lastSession: null, error: sessionQuery.error };
        }
      }
      return { status: 'anonymous' };
    }
    return { status: 'anonymous' };
  }, [sessionQuery.isSuccess, sessionQuery.data, sessionQuery.isPending, sessionQuery.error]);

  const state = overridden ?? liveState;
  const session = state.status === 'authenticated' ? state.session : null;
  const accountId = session?.userId ?? null;

  // Clear private data whenever the effective account changes, so a previous
  // account's cached data cannot leak into the next one.
  useEffect(() => {
    if (previousAccountId.current !== null && previousAccountId.current !== accountId) {
      queryClient.removeQueries({
        predicate: (query) => query.queryKey[0] === 'account',
      });
    }
    previousAccountId.current = accountId;
  }, [accountId, queryClient]);

  const refresh = useCallback(async (): Promise<SessionState> => {
    setOverridden(null);
    const result = await sessionQuery.refetch();
    if (result.data) return { status: 'authenticated', session: result.data };
    return liveState;
  }, [sessionQuery, liveState]);

  const clear = useCallback(() => {
    setOverridden({ status: 'anonymous' });
    queryClient.removeQueries({ predicate: (query) => query.queryKey[0] === 'account' });
    queryClient.setQueryData(queryKeys.session, undefined);
  }, [queryClient]);

  const broadcastChange = useCallback(() => {
    try {
      // Notify other tabs without broadcasting any sensitive data; the receivers
      // simply refetch the session from the server.
      localStorage.setItem(SESSION_STORAGE_KEY, String(Date.now()));
    } catch {
      /* Storage unavailable; other tabs keep their own state. */
    }
  }, []);

  // Cross-tab session changes: refetch on a storage signal, and ask other tabs
  // to refetch after this tab changed the session.
  useEffect(() => {
    const onStorage = (event: StorageEvent): void => {
      if (event.key === SESSION_STORAGE_KEY) {
        void sessionQuery.refetch();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [sessionQuery]);

  const value = useMemo<SessionContextValue>(
    () => ({
      state,
      accountId,
      isAdmin: session ? session.role === 'admin' && !session.impersonating : false,
      isImpersonating: session?.impersonating ?? false,
      actorIsAdmin: session?.role === 'admin',
      refresh,
      clear,
      broadcastChange,
    }),
    [state, accountId, session, refresh, clear, broadcastChange],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

/** Access the session context. Throws outside the provider. */
export function useSession(): SessionContextValue {
  const value = useContext(SessionContext);
  if (!value) {
    throw new Error('useSession must be used inside SessionProvider');
  }
  return value;
}
